import { useApolloClient } from "@apollo/client";
import { getDataOrNull, notNull, omitDeep } from "@msys/common";
import {
  DataGrid,
  GridColDef,
  Modal,
  StateStore,
  useLocalStorageAsState,
  useScreenWidth,
  VisibilityIndicatorIcon,
} from "@msys/ui";
import {
  Functions as FunctionsIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import { Badge, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { capitalize } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  DefineItemPropsOverridesEntryInput,
  namedOperations,
  Props2,
  Props2AllowedValuesNumber,
  Props2AllowedValuesText,
} from "../../../../clients/graphqlTypes.js";
import { FullScreenToggleIconButton } from "../../../commons/button/FullScreenToggleIconButton.js";
import { useAsyncCallbackWithStatus } from "../../../commons/hooks/useCallbackWithStatus.js";
import { useDataGridStateStore } from "../../users/useDataGridStateStore.js";
import { Props2NonComputedAllFragment } from "../properties.generated.js";
import {
  getPropertyType,
  groupProperties,
  hasPropOverride,
  isArrayType,
  isNonComputedProp,
  isNumberType,
  isTextType,
  ungroupProperties,
} from "../properties.js";
import { usePropertyHelpers } from "../usePropertyHelpers.js";
import {
  getQuestionControlLabel,
  useQuestionContol,
} from "../useQuestionContol.js";
import { ItemPropsCustomizeOverrideModal } from "./ItemPropsCustomizeOverrideModal.js";
import {
  useDefineItemPropsOverridesMutation,
  useInvalidatePrefixOrgCacheMutation,
  useItemPropsOverrideModalQuery,
} from "./ItemPropsOverrideModal.generated.js";

interface Props {
  templateId: string;
  itemId: string;
  versionNumber: number;
  handleClose(): void;
}

export function ItemPropsOverrideModal({
  templateId,
  itemId,
  versionNumber,
  handleClose,
}: Props) {
  const { t } = useTranslate(["Global", "QuoteItem"]);
  const { isMaxPhone } = useScreenWidth();
  const { enqueueSnackbar } = useSnackbar();

  const [shouldInvalidateCache, setShouldInvalidateCache] =
    React.useState(false);

  const stateStore = useDataGridStateStore("ItemPropsOverrideModal");

  const [openPropertyKey, setOpenPropertyKey] = React.useState<string | null>(
    null
  );

  const [isFullScreen, setIsFullScreen] = useLocalStorageAsState<boolean>(
    "msys-item-properties-override-modal-fullscreen",
    false,
    true
  );

  const client = useApolloClient();
  const query = useItemPropsOverrideModalQuery({
    client,
    variables: {
      docId: templateId,
      itemId: itemId,
      versionNumber: versionNumber,
      applyOverrides: true,
    },
    fetchPolicy: "no-cache",
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const item = quoteTemplate?.item?.[0];
  const customizableProps = React.useMemo(
    () =>
      item?.props2
        .filter(
          property => isNonComputedProp(property) && property.customizable
        )
        .map(prop => {
          const itemPropOverride = item.itemPropsOverrides.find(
            po => po.key === prop.key
          );

          return {
            ...prop,
            hasOverride: itemPropOverride
              ? hasPropOverride(itemPropOverride)
              : false,
          };
        }) ?? [],
    [item?.itemPropsOverrides, item?.props2]
  );
  const groupedCustomizableProps = React.useMemo(
    () => groupProperties(customizableProps),
    [customizableProps]
  );

  const [previousPropKey, nextPropKey] = React.useMemo(() => {
    const orderedPropertyKeys = ungroupProperties(groupedCustomizableProps).map(
      prop => prop.key
    );

    const currentKeyIndex = openPropertyKey
      ? orderedPropertyKeys.findIndex(key => key === openPropertyKey)
      : null;

    const previousKey =
      currentKeyIndex !== null
        ? orderedPropertyKeys[currentKeyIndex - 1]
        : null;
    const nextKey =
      currentKeyIndex !== null
        ? orderedPropertyKeys[currentKeyIndex + 1]
        : null;

    return [previousKey, nextKey];
  }, [groupedCustomizableProps, openPropertyKey]);

  const [defineItemPropsOverrides] = useDefineItemPropsOverridesMutation({
    client,
    refetchQueries: [namedOperations.Query.ItemPropsOverrideModal],
  });
  const handleSave = React.useCallback(
    async (newPropertyOverrides: DefineItemPropsOverridesEntryInput | null) => {
      if (!item) {
        throw new Error("Item not found");
      }

      const newOverrides: DefineItemPropsOverridesEntryInput[] = [
        ...item.itemPropsOverrides
          .map(o => {
            if (o.key === openPropertyKey) {
              return null;
            }
            const override = omitDeep(o, "__typename");
            return {
              ...override,
              allowedValuesMedia: override.allowedValuesMedia.map(avm =>
                omitDeep(avm, "id", "__typename")
              ),
            };
          })
          .filter(notNull),
      ];

      if (newPropertyOverrides) {
        newOverrides.push(newPropertyOverrides);
      }

      await defineItemPropsOverrides({
        variables: {
          input: {
            itemId,
            templateId,
            versionNumber,
            overrides: newOverrides,
          },
        },
        awaitRefetchQueries: false,
      });

      enqueueSnackbar(t("Overrides applied", { ns: "QuoteItem" }));

      setShouldInvalidateCache(true);
    },
    [
      defineItemPropsOverrides,
      enqueueSnackbar,
      item,
      itemId,
      openPropertyKey,
      t,
      templateId,
      versionNumber,
    ]
  );

  const [invalidatePrefixOrgCache] = useInvalidatePrefixOrgCacheMutation({
    client,
  });

  const [handleCloseModal, isProcessing] = useAsyncCallbackWithStatus(
    React.useCallback(async () => {
      if (shouldInvalidateCache) {
        await invalidatePrefixOrgCache();

        // we need refetch observable queries since applyOverrides are not in normalized cache
        await client.reFetchObservableQueries();
      }

      handleClose();
    }, [client, handleClose, invalidatePrefixOrgCache, shouldInvalidateCache])
  );

  return (
    <Modal
      title={t("Customise properties", { ns: "QuoteItem" })}
      actionButtons={[
        {
          label: t("Close", {
            ns: "Global",
          }),
          handleClick: handleCloseModal,
          buttonProps: {
            variant: "text",
            loading: isProcessing,
          },
        },
      ]}
      maxWidth="xl"
      dialogProps={
        !isMaxPhone && isFullScreen ? { fullScreen: true } : undefined
      }
      headerActions={
        !isMaxPhone ? (
          <FullScreenToggleIconButton
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
          />
        ) : undefined
      }
      alwaysVisible
      handleClose={handleCloseModal}
    >
      {item && (
        <>
          <Stack direction={"column"} spacing={2}>
            {groupedCustomizableProps.map(group => {
              return (
                <Stack
                  direction="column"
                  spacing={1}
                  key={`group-${group.key}`}
                >
                  <Typography variant="h3">
                    {group.key || t("Non-grouped", { ns: "QuoteItem" })}
                  </Typography>
                  <PropertiesTable
                    properties={group.properties}
                    setOpenPropertyKey={setOpenPropertyKey}
                    stateStore={stateStore}
                  />
                </Stack>
              );
            })}
          </Stack>
          {openPropertyKey && (
            <ItemPropsCustomizeOverrideModal
              templateId={templateId}
              itemId={itemId}
              versionNumber={versionNumber}
              propertyKey={openPropertyKey}
              override={
                item.itemPropsOverrides.find(
                  po => po.key === openPropertyKey
                ) || null
              }
              handleSave={handleSave}
              handlePrevious={
                previousPropKey
                  ? () => {
                      setOpenPropertyKey(previousPropKey);
                    }
                  : null
              }
              handleNext={
                nextPropKey
                  ? () => {
                      setOpenPropertyKey(nextPropKey);
                    }
                  : null
              }
              handleClose={() => setOpenPropertyKey(null)}
            />
          )}
        </>
      )}
    </Modal>
  );
}

type PropertiesTableItem = Props2 & { hasOverride: boolean };
const PropertiesTable = React.memo(
  ({
    properties,
    setOpenPropertyKey,
    stateStore,
  }: {
    properties: PropertiesTableItem[];
    setOpenPropertyKey: (key: string) => void;
    stateStore: StateStore;
  }) => {
    const { t } = useTranslate(["Global", "QuoteItem"]);
    const { getBoolLabel, getUnitLabel } = usePropertyHelpers();
    const { askWhomLabels, askWhenLabels } = useQuestionContol();

    const columns = React.useMemo(
      (): GridColDef<PropertiesTableItem>[] => [
        {
          field: "label",
          headerName: t("Label", {
            ns: "Global",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          renderCell({ row: property }) {
            return (
              <Typography
                variant="body2"
                color={property.hasOverride ? "secondary" : "primary"}
              >
                {property.label}
              </Typography>
            );
          },
        },
        {
          field: "type",
          headerName: t("Type", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          valueGetter(value, property) {
            return getPropertyType(property);
          },
          valueFormatter(value) {
            return capitalize(value);
          },
        },

        {
          field: "unit",
          headerName: t("Unit", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 1,
          valueGetter(value, property) {
            return isNumberType(property) && property.unit
              ? property.unit
              : null;
          },
          valueFormatter(value) {
            return value ? getUnitLabel(value) : "";
          },
        },
        {
          field: "allowMultiple",
          headerName: t("Allow multiple", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isArrayType(property);
          },
          valueFormatter(value) {
            return value === true || value === false ? getBoolLabel(value) : "";
          },
        },
        {
          field: "allowedValues",
          headerName: t("Allowed values", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isNonComputedProp(property)
              ? isTextType(property)
                ? property.allowedValuesText
                : isNumberType(property)
                  ? property.allowedValuesNumber
                  : null
              : null;
          },
          valueFormatter(
            value:
              | Props2AllowedValuesText[]
              | Props2AllowedValuesNumber[]
              | null
          ) {
            if (value === null) return "";
            return value && value.length > 0
              ? value
                  .map(v =>
                    "allowedText" in v
                      ? v.allowedText
                      : "allowedNumber" in v
                        ? v.allowedNumber
                        : ""
                  )
                  .join(", ")
              : "";
          },
        },
        {
          field: "prompt",
          headerName: t("Prompt", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isNonComputedProp(property) ? property.prompt : null;
          },
        },
        {
          field: "questionControl",
          headerName: t("Question control", {
            ns: "QuoteItem",
          }),
          sortable: false,
          groupable: false,
          flex: 2,
          valueGetter(value, property) {
            return isNonComputedProp(property)
              ? property.questionControl
              : null;
          },
          valueFormatter(
            value: Props2NonComputedAllFragment["questionControl"] | null
          ) {
            if (value === null) return "";
            return getQuestionControlLabel(value, {
              askWhenLabels,
              askWhomLabels,
            });
          },
        },
        {
          field: "group",
          headerName: t("Group", { ns: "QuoteItem" }),
          sortable: false,
          groupable: false,
          // hideable: true,
          flex: 1,
          valueGetter(value, property) {
            return property.group;
          },
        },
        {
          field: "askIfExpr",
          headerName: t("Ask if", { ns: "QuoteItem" }),
          sortable: false,
          groupable: false,
          flex: 0,
          // minWidth: 34,
          valueGetter(value, property) {
            return isNonComputedProp(property) && Boolean(property.askIfExpr);
          },
          renderCell({ row: property }) {
            return isNonComputedProp(property) && property.askIfExpr ? (
              <FunctionsIcon color="secondary" />
            ) : null;
          },
        },
        {
          field: "visibility",
          headerName: t("Visibility", { ns: "Global" }),
          sortable: false,
          groupable: false,
          flex: 0,
          // minWidth: 34,
          valueGetter(value, property) {
            return property.clientVisibility;
          },
          renderCell({ row: property }) {
            return (
              <VisibilityIndicatorIcon isVisible={property.clientVisibility} />
            );
          },
        },
        {
          field: "configure",
          headerName: "",
          sortable: false,
          groupable: false,
          flex: 0,
          minWidth: 34,
          maxWidth: 34,
          renderCell({ row: property }) {
            return (
              <IconButton
                color="primary"
                size={"small"}
                sx={{ marginLeft: "-10px" }}
                onClick={() => {
                  setOpenPropertyKey(property.key);
                }}
              >
                <Badge
                  color="secondary"
                  variant="dot"
                  invisible={!property.hasOverride}
                >
                  <Tooltip title={t("Customise property", { ns: "QuoteItem" })}>
                    <SettingsIcon />
                  </Tooltip>
                </Badge>
              </IconButton>
            );
          },
        },
      ],
      [
        t,
        getUnitLabel,
        getBoolLabel,
        askWhenLabels,
        askWhomLabels,
        setOpenPropertyKey,
      ]
    );

    return (
      <DataGrid<PropertiesTableItem>
        stateStore={stateStore}
        columns={columns}
        // disableColumnMenu
        disableVirtualization
        rows={properties}
        getRowId={row => row.key}
        paginationMode="client"
        sortingMode="client"
        disableRowSelectionOnClick
      />
    );
  }
);
